import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {WardState} from "@/modules/regionsv2/ward/state";
import {IdMapper} from "@/interfaces/state/base";
import {Ward} from "@/interfaces/entity/ward";
import {ChangeStatusProp, PaginationMeta} from "@/interfaces/common";
import {WardParams} from "@/modules/regionsv2/ward/params";
import {
    createWardV21, deleteWardV21,
    editWardV21, listWardV21,
} from "@/modules/regionsv2/ward/services";
import {union, cloneDeep, set} from "lodash";
import {getWardBySlugsAndDistrictIds} from "@/modules/regions/services";

@Module({ dynamic: true, name: "ward", store })
class WardModule extends VuexModule implements WardState {
    data: IdMapper<Ward> = {};
    ids: number[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: WardParams = new WardParams();
    mapIdsByDistrictId: IdMapper<Array<number>> = {};
    cachedData: IdMapper<Ward> = {};
    loadingStatus: IdMapper<LoadingStatus> = {};

    get wards() {
        return this.ids.map((id) => this.data[id]);
    }

    @Mutation
    private MAP_WARDS_V21(payload: any) {
        const data: IdMapper<Ward> = { ...this.data };
        this.meta = payload.meta;
        this.ids = payload.datas.map((ward: Ward) => {
            data[ward.id] = {
                ...ward,
                isDeleting: false,
                isSaving: false,
                editable: false
            } as any;
            return ward.id;
        });
        this.data = data;
        this.cachedData = cloneDeep(data);
    }

    @Mutation
    private MAP_WARD_BY_DISTRICT_V21(payload: Ward[]) {
        const data: IdMapper<Ward> = cloneDeep(this.data);
        const mapIdsByDistrictId = cloneDeep(this.mapIdsByDistrictId);
        payload.forEach((ward: Ward) => {
            data[ward.id] = ward;
            mapIdsByDistrictId[ward.districtId] = union(mapIdsByDistrictId[ward.districtId], [ward.id]);
        });
        this.data = data;
        this.mapIdsByDistrictId = mapIdsByDistrictId;
    }

    @Mutation
    private UPDATE_WARD_PARAMS_V21(params: any) {
        this.params = { ...params };
    }

    @Mutation
    private UPDATE_DATA_WARD_V21({ key, value }: any) {
        this.data[key] = value;
    }

    @Mutation
    private UPDATE_CACHED_DATA_WARD_V21({ key, value }: any) {
        this.cachedData[key] = value;
    }

    @Mutation
    private SET_WARD_V21(payload: Ward) {
        this.ids = union([payload.id], this.ids);
        this.data = { ...this.data, [payload.id]: payload }
    }

    @Mutation
    private DELETE_WARD_V21(payload: Ward) {
        this.ids = this.ids.filter((id) => id != payload.id);
        delete this.data[payload.id];
    }

    @Mutation
    private CHANGE_LOADING_STATUS_WARD_V21({key, value }: ChangeStatusProp) {
        set(this.loadingStatus, key, value);
    }

    @Action({ rawError: true })
    public async listWardsV21(params: any = {}) {
        const { data } = await listWardV21(params);
        this.MAP_WARDS_V21(data);
        return data.datas;
    }

    @Action({ rawError: true })
    public async listWardsByDistrictV21(districtId: number) {
        if (!this.loadingStatus[districtId] || this.loadingStatus[districtId] == 'waiting') {
            const params = { ...new WardParams(), districtId, limit: 1000 };
            this.CHANGE_LOADING_STATUS_WARD_V21({ key: `${districtId}`, value: "loading" });
            const { data } = await listWardV21(params);
            this.CHANGE_LOADING_STATUS_WARD_V21({ key: `${districtId}`, value: "loaded" });
            this.MAP_WARD_BY_DISTRICT_V21(data.datas);
            return data.datas;
        }
    }

    @Action({ rawError: true })
    public async createWardV21(payload: any) {
        const { data } = await createWardV21(payload);
        this.SET_WARD_V21({ ...data.data, isDeleting: false, isSaving: false, editable: false });
        return data.data;
    }

    @Action({ rawError: true })
    public async editWardV21(payload: any) {
        const { data } = await editWardV21(payload);
        this.SET_WARD_V21(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async deleteWardV21(id: number) {
        const { data } = await deleteWardV21(id);
        this.DELETE_WARD_V21(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async paramsChangeWardV21(params: any) {
        this.UPDATE_WARD_PARAMS_V21(params);
        await this.listWardsV21(params);
    }

    @Action({ rawError: true })
    public updateDataWardV21({ key, value }: any) {
        this.UPDATE_DATA_WARD_V21({ key, value });
    }

    @Action({ rawError: true })
    public updateCachedDataWardV21({ key, value }: any) {
        this.UPDATE_CACHED_DATA_WARD_V21({ key, value });
    }

    @Action({ rawError: true })
    public async getWardsForExcelV21({ districtIds, wardSlugs }: any) {
        const { data } = await getWardBySlugsAndDistrictIds(districtIds, wardSlugs);
        this.MAP_WARD_BY_DISTRICT_V21(data.datas);
        return data.datas;
    }
}

export const WardStore = getModule(WardModule)
